import { CheckOutlined, ReloadOutlined, RightOutlined, WarningOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '../api/errors';
import {
  acceptProjectTaskAssignment,
  listMyProjectTasks,
  projectTaskAssignmentRoleNames,
} from '../api/projects';
import type {
  MyProjectTaskItem,
  ProjectPriorityValue,
  ProjectTaskAssignmentRoleValue,
  ProjectTaskStatusValue,
} from '../api/projects';
import { AppRoutes } from '../routes';
import { BrandColors } from '../theme/brand';
import { formatDate, priorityTag } from './projects/projectDisplay';
import { taskStatusTag } from './projects/taskDisplay';

/**
 * Trang công việc được giao cho chính mình.
 *
 * <b>Đây là màn hình người làm mở đầu tiên trong ngày</b>, nên nó phải trả lời đúng một câu:
 * <i>hôm nay làm gì trước</i>. Máy chủ sắp sẵn theo thứ tự đó — việc quá hạn lên trước, rồi tới mức
 * ưu tiên, rồi tới hạn — nên trang này không sắp lại.
 *
 * <b>Mỗi dòng kèm mã và tên dự án.</b> Ở công ty nhỏ một kỹ sư chạy ba bốn dự án cùng lúc; một danh
 * sách việc không nói rõ việc nào của dự án nào thì đọc xong vẫn không biết bắt đầu từ đâu.
 *
 * <b>Việc chưa xác nhận được đánh dấu riêng.</b> Với công ty làm remote thì nút xác nhận này thay cho
 * câu <i>"em nhận việc này nhé"</i> trong tin nhắn — và nó cho người giao biết việc đã tới tay ai.
 */
export function MyTasksPage(): ReactNode {
  const navigate = useNavigate();

  const [tasks, setTasks] = useState<readonly MyProjectTaskItem[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [includeFinished, setIncludeFinished] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      setErrorMessage(null);

      try {
        const loaded = await listMyProjectTasks(includeFinished);

        if (isCurrent) {
          setTasks(loaded);
        }
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được danh sách việc của bạn.',
        );
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [includeFinished, reloadToken]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  async function accept(task: MyProjectTaskItem): Promise<void> {
    try {
      await acceptProjectTaskAssignment(task.projectId, task.taskId, task.assignmentId);

      message.success('Đã xác nhận nhận việc.');
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không xác nhận được.');
    }
  }

  const columns: TableColumnsType<MyProjectTaskItem> = [
    {
      title: 'Dự án',
      dataIndex: 'projectCode',
      width: 190,
      render: (projectCode: string, task) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong style={{ fontSize: 12 }}>
            {projectCode}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {task.projectName}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Công việc',
      dataIndex: 'taskName',
      render: (taskName: string, task) => (
        <Space size={4} wrap>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {task.wbsCode}
          </Typography.Text>
          <span>{taskName}</span>
          {task.isAccepted ? null : (
            <Tooltip title="Bạn chưa xác nhận nhận việc này. Người giao đang chờ biết việc đã tới tay bạn.">
              <Tag color="gold">Chưa nhận</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Vai trò',
      dataIndex: 'role',
      width: 160,
      render: (role: ProjectTaskAssignmentRoleValue) => projectTaskAssignmentRoleNames[role],
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 130,
      render: (status: ProjectTaskStatusValue, task) =>
        taskStatusTag(status, task.blockedReason),
    },
    {
      title: 'Ưu tiên',
      dataIndex: 'priority',
      width: 110,
      render: (priority: ProjectPriorityValue) => priorityTag(priority),
    },
    {
      title: 'Hạn',
      dataIndex: 'plannedEndDate',
      width: 130,
      render: (plannedEndDate: string | null, task) => (
        <Space size={4}>
          <span>{formatDate(plannedEndDate)}</span>
          {task.isOverdue ? (
            <Tooltip title="Đã quá ngày kế hoạch mà việc chưa xong.">
              <WarningOutlined style={{ color: BrandColors.red }} />
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Giờ còn lại',
      dataIndex: 'remainingEffortHours',
      width: 110,
      align: 'right',
      render: (hours: number | null) => hours ?? '—',
    },
    {
      title: '',
      key: 'actions',
      width: 190,
      render: (_, task) => (
        <Space size={4}>
          {task.isAccepted ? null : (
            <Button
              size="small"
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => {
                void accept(task);
              }}
            >
              Nhận việc
            </Button>
          )}
          <Tooltip title="Mở cây công việc của dự án">
            <Button
              size="small"
              icon={<RightOutlined />}
              onClick={() => {
                void navigate(`${AppRoutes.Project}/${task.projectId}/cong-viec`);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const unacceptedCount = tasks?.filter((task) => !task.isAccepted).length ?? 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Việc của tôi
        </Typography.Title>
        <Space wrap>
          <Checkbox
            checked={includeFinished}
            onChange={(event) => {
              setIncludeFinished(event.target.checked);
            }}
          >
            Hiện cả việc đã xong
          </Checkbox>
          <Button icon={<ReloadOutlined />} onClick={reload}>
            Tải lại
          </Button>
        </Space>
      </Space>

      {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

      {unacceptedCount > 0 ? (
        <Alert
          type="warning"
          showIcon
          message={`Bạn có ${unacceptedCount.toString()} việc chưa xác nhận nhận việc`}
          description="Người giao đang chờ biết việc đã tới tay bạn. Nếu việc nào không làm được, hãy nói ngay thay vì để nó nằm im."
        />
      ) : null}

      <Table<MyProjectTaskItem>
        rowKey="assignmentId"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={tasks ?? undefined}
        loading={tasks === null && errorMessage === null}
        locale={{
          emptyText: (
            <Empty
              description={
                includeFinished
                  ? 'Chưa có việc nào được giao cho bạn.'
                  : 'Không còn việc nào đang mở. Bật ô hiện cả việc đã xong để xem lại.'
              }
            />
          ),
        }}
      />

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Danh sách đã được sắp theo thứ tự nên làm: việc quá hạn trước, rồi tới mức ưu tiên, rồi tới
        hạn gần nhất.
      </Typography.Text>
    </Space>
  );
}
